import { useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface CarouselSlide {
  icon: string;
  heading: string;
  body: string;
}

interface CarouselProps {
  slides: CarouselSlide[];
}

export default function Carousel({ slides }: CarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 15000, stopOnInteraction: false }),
  ]);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  if (slides.length === 0) return null;

  return (
    <div className="flex items-center justify-center gap-2 2xl:gap-0 max-w-6xl mx-auto 2xl:max-w-none">
      {/* Left Chevron */}
      <button
        onClick={scrollPrev}
        className="flex-shrink-0 p-2 text-white/70 hover:text-white transition-colors"
        aria-label="Previous slide"
      >
        <FiChevronLeft className="w-8 h-8" />
      </button>

      {/* Embla Viewport */}
      <div className="flex-1 2xl:flex-none 2xl:w-full 2xl:max-w-6xl overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {slides.map((slide, index) => (
            <div key={index} className="flex-[0_0_100%] min-w-0 px-4 md:px-0">
              <div className="flex flex-col items-center text-center gap-4 md:flex-row md:items-center md:text-left md:gap-8">
                {/* Icon */}
                <div className="flex-shrink-0 flex justify-center md:w-[10%]">
                  <img
                    src={slide.icon}
                    alt=""
                    className="w-[6.25rem] object-contain"
                  />
                </div>

                {/* Heading */}
                <div className="flex-shrink-0 md:w-[20%]">
                  <h3
                    className="text-xl font-bold text-white"
                    dangerouslySetInnerHTML={{ __html: slide.heading }}
                  />
                </div>

                {/* Body - 7 parts (flex-1 takes remaining ~65%) */}
                <div className="md:flex-1">
                  <p
                    className="text-white/90 text-sm leading-relaxed [&_strong]:font-bold [&_strong]:underline"
                    dangerouslySetInnerHTML={{ __html: slide.body }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Chevron */}
      <button
        onClick={scrollNext}
        className="flex-shrink-0 p-2 text-white/70 hover:text-white transition-colors"
        aria-label="Next slide"
      >
        <FiChevronRight className="w-8 h-8" />
      </button>
    </div>
  );
}
